/**
 * Редактор списка шагов правила. Шаги добавляются/удаляются/двигаются вверх-вниз.
 * Для каждого типа — своя форма конфигурации.
 *
 * В items.update фильтр и список действий редактируются визуально: поля
 * подгружаются лениво по выбранному itemType, ссылки разрешаются через
 * словари. JSON наружу больше не торчит.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Empty,
  Input,
  Select,
  Space,
  Typography,
} from 'antd';
import { ArrowDownOutlined, ArrowUpOutlined, DeleteOutlined, FilterOutlined, PlusOutlined } from '@ant-design/icons';
import type {
  AutomationStepType,
  FieldDefinition,
  FilterLeaf,
  ItemTypeListItem,
  SpaceMember,
  View as ViewDto,
  JiraPullMode,
} from '@lens/shared';
import {
  FilterValueInput,
  OPERATOR_LABELS,
  getOperatorsForFieldType,
} from '../../shared/components/FilterRow.tsx';
import { listFields } from '../item-types/api.ts';
import { getDictionary, listEntries } from '../dictionaries/api.ts';
import { listSavedQueries, type SavedPullQuery } from '../jira/api.ts';
import { entryLabel } from '../dictionaries/labelHelpers.ts';

const PULL_MODE_OPTIONS: Array<{ label: string; value: JiraPullMode }> = [
  { label: 'Создавать новые и обновлять существующие', value: 'create_and_update' },
  { label: 'Только создавать новые', value: 'create_only' },
];

export type EditableStep =
  | {
      type: 'items.update';
      config: {
        itemTypeId?: string;
        filter?: unknown[];
        actions?: Array<{ fieldSlug: string; value: unknown }>;
      };
      continueOnError: boolean;
    }
  | {
      type: 'jira.pull';
      config: {
        itemTypeId?: string;
        jql?: string;
        mode?: JiraPullMode;
        runAsUserId?: string;
      };
      continueOnError: boolean;
    }
  | {
      type: 'jira.push';
      config: {
        viewId?: string;
        changedSinceLastRun?: boolean;
        runAsUserId?: string;
      };
      continueOnError: boolean;
    };

interface Props {
  spaceId: string;
  steps: EditableStep[];
  onChange: (steps: EditableStep[]) => void;
  itemTypes: ItemTypeListItem[];
  views: ViewDto[];
  members: SpaceMember[];
  /** Включена ли Jira-интеграция — гейтит шаги Pull/Push в Jira. */
  jiraEnabled?: boolean;
}

type RefOption = { value: string; label: string };

const STEP_TYPE_LABEL: Record<AutomationStepType, string> = {
  'items.update': 'Изменить значения полей',
  'jira.pull': 'Подтянуть из Jira (JQL)',
  'jira.push': 'Выгрузить в Jira (push view)',
};

function emptyStep(type: AutomationStepType): EditableStep {
  if (type === 'items.update') {
    return {
      type,
      config: { filter: [], actions: [{ fieldSlug: '', value: '' }] },
      continueOnError: false,
    };
  }
  if (type === 'jira.pull') {
    return { type, config: { mode: 'create_and_update' }, continueOnError: false };
  }
  return { type: 'jira.push', config: { changedSinceLastRun: true }, continueOnError: false };
}

export function RuleStepsEditor({
  spaceId,
  steps,
  onChange,
  itemTypes,
  views,
  members,
  jiraEnabled = false,
}: Props): JSX.Element {
  // Кэш определений полей по itemTypeId — чтобы не дёргать /fields на каждый рендер.
  const [fieldsByItemType, setFieldsByItemType] = useState<Record<string, FieldDefinition[]>>({});
  // Кэш опций словарей (для reference-полей).
  const [refOptionsByDict, setRefOptionsByDict] = useState<Record<string, RefOption[]>>({});

  const ensureFields = useCallback(
    async (itemTypeId: string) => {
      if (fieldsByItemType[itemTypeId]) return;
      try {
        const list = await listFields(spaceId, itemTypeId);
        setFieldsByItemType((prev) => ({ ...prev, [itemTypeId]: list }));
      } catch {
        setFieldsByItemType((prev) => ({ ...prev, [itemTypeId]: [] }));
      }
    },
    [spaceId, fieldsByItemType],
  );

  const ensureRefOptions = useCallback(
    async (field: FieldDefinition) => {
      const dictionaryId = (field.config as { dictionaryId?: string } | null)?.dictionaryId;
      if (!dictionaryId || refOptionsByDict[dictionaryId]) return;
      try {
        const [dict, res] = await Promise.all([
          getDictionary(dictionaryId),
          listEntries(dictionaryId, { limit: 200 }),
        ]);
        const opts: RefOption[] = res.items.map((e) => ({
          value: e.id,
          label: entryLabel(e, dict),
        }));
        setRefOptionsByDict((prev) => ({ ...prev, [dictionaryId]: opts }));
      } catch {
        setRefOptionsByDict((prev) => ({ ...prev, [dictionaryId]: [] }));
      }
    },
    [refOptionsByDict],
  );


  const updateStep = useCallback(
    (idx: number, patch: Partial<EditableStep>) => {
      const next = steps.slice();
      next[idx] = { ...next[idx], ...patch } as EditableStep;
      onChange(next);
    },
    [steps, onChange],
  );

  const updateConfig = useCallback(
    (idx: number, configPatch: Record<string, unknown>) => {
      const next = steps.slice();
      const cur = next[idx];
      if (!cur) return;
      next[idx] = {
        ...cur,
        config: { ...(cur.config as object), ...configPatch },
      } as EditableStep;
      onChange(next);
    },
    [steps, onChange],
  );

  const move = useCallback(
    (idx: number, dir: -1 | 1) => {
      const j = idx + dir;
      if (j < 0 || j >= steps.length) return;
      const next = steps.slice();
      [next[idx], next[j]] = [next[j]!, next[idx]!];
      onChange(next);
    },
    [steps, onChange],
  );

  const remove = useCallback(
    (idx: number) => {
      const next = steps.slice();
      next.splice(idx, 1);
      onChange(next);
    },
    [steps, onChange],
  );

  const add = useCallback(
    (type: AutomationStepType) => {
      onChange([...steps, emptyStep(type)]);
    },
    [steps, onChange],
  );

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      {steps.length === 0 ? (
        <Empty description="Шаги не добавлены" />
      ) : (
        steps.map((step, idx) => (
          <Card
            key={idx}
            size="small"
            title={`Шаг ${idx + 1} · ${STEP_TYPE_LABEL[step.type]}`}
            extra={
              <Space>
                <Button
                  size="small"
                  icon={<ArrowUpOutlined />}
                  onClick={() => move(idx, -1)}
                  disabled={idx === 0}
                />
                <Button
                  size="small"
                  icon={<ArrowDownOutlined />}
                  onClick={() => move(idx, 1)}
                  disabled={idx === steps.length - 1}
                />
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => remove(idx)}
                />
              </Space>
            }
          >
            {step.type === 'items.update' && (
              <ItemsUpdateForm
                step={step}
                itemTypes={itemTypes}
                fields={
                  step.config.itemTypeId ? fieldsByItemType[step.config.itemTypeId] ?? null : null
                }
                ensureFields={ensureFields}
                refOptionsByDict={refOptionsByDict}
                ensureRefOptions={ensureRefOptions}
                onConfigChange={(p) => updateConfig(idx, p)}
              />
            )}
            {step.type === 'jira.pull' && (
              <JiraPullForm
                step={step}
                spaceId={spaceId}
                itemTypes={itemTypes}
                members={members}
                onConfigChange={(p) => updateConfig(idx, p)}
              />
            )}
            {step.type === 'jira.push' && (
              <JiraPushForm
                step={step}
                views={views}
                members={members}
                onConfigChange={(p) => updateConfig(idx, p)}
              />
            )}
            <div style={{ marginTop: 12 }}>
              <Checkbox
                checked={step.continueOnError}
                onChange={(e) => updateStep(idx, { continueOnError: e.target.checked } as Partial<EditableStep>)}
              >
                Продолжать при ошибке (не останавливать цепочку, если этот шаг упадёт)
              </Checkbox>
            </div>
          </Card>
        ))
      )}

      <Space wrap>
        <Button icon={<PlusOutlined />} onClick={() => add('items.update')}>
          Изменить поля
        </Button>
        {jiraEnabled && (
          <>
            <Button icon={<PlusOutlined />} onClick={() => add('jira.pull')}>
              Pull из Jira
            </Button>
            <Button icon={<PlusOutlined />} onClick={() => add('jira.push')}>
              Push в Jira
            </Button>
          </>
        )}
      </Space>
    </Space>
  );
}

// ─── items.update ──────────────────────────────────────────────────────

function ItemsUpdateForm({
  step,
  itemTypes,
  fields,
  ensureFields,
  refOptionsByDict,
  ensureRefOptions,
  onConfigChange,
}: {
  step: Extract<EditableStep, { type: 'items.update' }>;
  itemTypes: ItemTypeListItem[];
  fields: FieldDefinition[] | null;
  ensureFields: (itemTypeId: string) => Promise<void>;
  refOptionsByDict: Record<string, RefOption[]>;
  ensureRefOptions: (field: FieldDefinition) => Promise<void>;
  onConfigChange: (p: Record<string, unknown>) => void;
}): JSX.Element {
  const actions = step.config.actions ?? [];
  const filter = (step.config.filter ?? []) as FilterLeaf[];
  const itemTypeId = step.config.itemTypeId;

  const setActions = (next: Array<{ fieldSlug: string; value: unknown }>) =>
    onConfigChange({ actions: next });
  const setFilter = (next: FilterLeaf[]) => onConfigChange({ filter: next });

  // Подгрузить fieldDefinitions для выбранного itemType.
  useEffect(() => {
    if (itemTypeId) void ensureFields(itemTypeId);
  }, [itemTypeId, ensureFields]);

  // Подгрузить опции для всех reference-полей, упомянутых в фильтре/действиях.
  useEffect(() => {
    if (!fields) return;
    const slugs = new Set<string>([
      ...filter.map((f) => f.fieldSlug),
      ...actions.map((a) => a.fieldSlug),
    ]);
    for (const slug of slugs) {
      const fd = fields.find((x) => x.slug === slug);
      if (fd?.fieldType === 'reference') void ensureRefOptions(fd);
    }
  }, [fields, filter, actions, ensureRefOptions]);

  const fieldOptions = (fields ?? []).map((f) => ({ value: f.slug, label: f.name }));
  const fieldsLoaded = fields !== null;
  const noItemType = !itemTypeId;

  const refOptionsFor = (fd: FieldDefinition | null): RefOption[] => {
    const dictId = (fd?.config as { dictionaryId?: string } | null)?.dictionaryId;
    return dictId ? refOptionsByDict[dictId] ?? [] : [];
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="small">
      <Typography.Text strong>Тип записи</Typography.Text>
      <Select
        placeholder="Выберите тип записи"
        value={itemTypeId}
        options={itemTypes.map((it) => ({ value: it.id, label: it.name }))}
        onChange={(v) => onConfigChange({ itemTypeId: v, filter: [], actions: [] })}
        style={{ width: '100%' }}
        showSearch
        optionFilterProp="label"
      />

      <Typography.Text strong style={{ marginTop: 8 }}>
        Фильтр (какие записи менять)
      </Typography.Text>
      {noItemType ? (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Сначала выберите тип записи.
        </Typography.Text>
      ) : !fieldsLoaded ? (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Загрузка полей…
        </Typography.Text>
      ) : (
        <FilterRowsEditor
          fields={fields ?? []}
          filter={filter}
          onChange={setFilter}
          refOptionsFor={refOptionsFor}
          fieldOptions={fieldOptions}
        />
      )}

      <Typography.Text strong style={{ marginTop: 8 }}>
        Действия (что менять)
      </Typography.Text>
      {noItemType ? (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Сначала выберите тип записи.
        </Typography.Text>
      ) : !fieldsLoaded ? null : (
        <>
          {actions.map((a, ai) => {
            const fd = (fields ?? []).find((x) => x.slug === a.fieldSlug) ?? null;
            return (
              <Space key={ai} style={{ width: '100%' }} align="start" wrap>
                <Select
                  size="small"
                  showSearch
                  optionFilterProp="label"
                  placeholder="Поле"
                  value={a.fieldSlug || undefined}
                  options={fieldOptions}
                  style={{ width: 220 }}
                  onChange={(slug) => {
                    const next = actions.slice();
                    next[ai] = { fieldSlug: slug, value: undefined };
                    setActions(next);
                  }}
                />
                <ActionValueInput
                  field={fd}
                  value={a.value}
                  onChange={(v) => {
                    const next = actions.slice();
                    next[ai] = { ...a, value: v };
                    setActions(next);
                  }}
                  refOptions={refOptionsFor(fd)}
                />
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => setActions(actions.filter((_, i) => i !== ai))}
                />
              </Space>
            );
          })}
          <Button
            size="small"
            icon={<PlusOutlined />}
            onClick={() => setActions([...actions, { fieldSlug: '', value: undefined }])}
          >
            Добавить действие
          </Button>
        </>
      )}
    </Space>
  );
}

// ─── Визуальный редактор фильтра ────────────────────────────────────────

function FilterRowsEditor({
  fields,
  filter,
  onChange,
  refOptionsFor,
  fieldOptions,
}: {
  fields: FieldDefinition[];
  filter: FilterLeaf[];
  onChange: (next: FilterLeaf[]) => void;
  refOptionsFor: (fd: FieldDefinition | null) => RefOption[];
  fieldOptions: Array<{ value: string; label: string }>;
}): JSX.Element {
  const update = (idx: number, patch: Partial<FilterLeaf>) => {
    onChange(filter.map((f, i) => (i === idx ? { ...f, ...patch } : f)));
  };
  const remove = (idx: number) => onChange(filter.filter((_, i) => i !== idx));
  const add = () => {
    const first = fields[0];
    if (!first) return;
    const ops = getOperatorsForFieldType(first.fieldType);
    onChange([
      ...filter,
      {
        fieldSlug: first.slug,
        operator: ops[0] ?? 'eq',
        value: undefined,
        ...(filter.length > 0 ? { combinator: 'and' as const } : {}),
      },
    ]);
  };

  if (fields.length === 0) {
    return (
      <Alert
        type="warning"
        showIcon={false}
        message="У выбранного типа записи нет полей."
        style={{ fontSize: 12 }}
      />
    );
  }

  return (
    <div className="lens-filter-editor" style={{ width: '100%' }}>
      {filter.length > 0 && (
        <div className="lens-filter-editor__head">
          <span className="lens-filter-editor__title">
            <FilterOutlined style={{ fontSize: 12 }} />
            Фильтр
            <span className="lens-filter-editor__count">{filter.length}</span>
          </span>
          <div className="lens-filter-editor__head-actions">
            <Button
              size="small"
              type="text"
              danger
              onClick={() => onChange([])}
              className="lens-filter-editor__clear"
            >
              Очистить всё
            </Button>
          </div>
        </div>
      )}
      {filter.length === 0 ? (
        <Typography.Text type="secondary" style={{ fontSize: 12, padding: '4px 6px' }}>
          Без условий — будут затронуты все записи этого типа.
        </Typography.Text>
      ) : (
        filter.map((f, i) => {
          const fd = fields.find((x) => x.slug === f.fieldSlug) ?? null;
          const operators = fd ? getOperatorsForFieldType(fd.fieldType) : ['eq', 'neq'];
          const noValue = ['is_empty', 'is_not_empty'].includes(f.operator);
          return (
            <div key={i} className="lens-filter-editor__row">
              {i === 0 ? (
                <span className="lens-filter-editor__lead">где</span>
              ) : (
                <Select
                  size="small"
                  style={{ width: 72, flexShrink: 0 }}
                  className="lens-filter-editor__combinator"
                  value={f.combinator === 'or' ? 'or' : 'and'}
                  onChange={(v) => update(i, { combinator: v as 'and' | 'or' })}
                  options={[
                    { value: 'and', label: 'И' },
                    { value: 'or', label: 'ИЛИ' },
                  ]}
                />
              )}
              <Select
                size="small"
                style={{ flex: '0 1 220px', minWidth: 160 }}
                showSearch
                optionFilterProp="label"
                value={f.fieldSlug}
                onChange={(slug) => {
                  const newField = fields.find((x) => x.slug === slug);
                  const ops = newField ? getOperatorsForFieldType(newField.fieldType) : ['eq'];
                  update(i, { fieldSlug: slug, operator: ops[0] ?? 'eq', value: undefined });
                }}
                options={fieldOptions}
                placeholder="Поле"
              />
              <Select
                size="small"
                style={{ flex: '0 1 140px', minWidth: 120 }}
                value={f.operator}
                onChange={(op) =>
                  update(i, {
                    operator: op,
                    value: ['is_empty', 'is_not_empty'].includes(op) ? undefined : f.value,
                  })
                }
                options={operators.map((op) => ({ value: op, label: OPERATOR_LABELS[op] ?? op }))}
              />
              {!noValue && (
                <FilterValueInput
                  field={fd}
                  operator={f.operator}
                  value={f.value}
                  onChange={(v) => update(i, { value: v })}
                  refOptions={refOptionsFor(fd)}
                />
              )}
              <Button
                size="small"
                type="text"
                icon={<DeleteOutlined />}
                onClick={() => remove(i)}
                aria-label="Удалить условие"
                className="lens-filter-editor__remove"
              />
            </div>
          );
        })
      )}
      <div>
        <Button
          size="small"
          type="dashed"
          icon={<PlusOutlined />}
          onClick={add}
          className="lens-filter-editor__add"
        >
          Добавить условие
        </Button>
      </div>
    </div>
  );
}

// ─── Виджет значения для действия items.update ──────────────────────────
//
// Семантика action.value — это запись в поле, поэтому используем оператор `eq`
// для FilterValueInput: получаем нативный виджет (DatePicker для date, Select
// для select/reference/boolean, number-input для number и т.д.).
function ActionValueInput({
  field,
  value,
  onChange,
  refOptions,
}: {
  field: FieldDefinition | null;
  value: unknown;
  onChange: (v: unknown) => void;
  refOptions: RefOption[];
}): JSX.Element {
  if (!field) {
    return (
      <Input
        size="small"
        placeholder="Сначала выберите поле"
        disabled
        style={{ width: 320 }}
      />
    );
  }
  return (
    <FilterValueInput
      field={field}
      operator="eq"
      value={value}
      onChange={onChange}
      refOptions={refOptions}
    />
  );
}

// ─── jira.pull ──────────────────────────────────────────────────────────

function JiraPullForm({
  step,
  spaceId,
  itemTypes,
  members,
  onConfigChange,
}: {
  step: Extract<EditableStep, { type: 'jira.pull' }>;
  spaceId: string;
  itemTypes: ItemTypeListItem[];
  members: SpaceMember[];
  onConfigChange: (p: Record<string, unknown>) => void;
}): JSX.Element {
  const [savedQueries, setSavedQueries] = useState<SavedPullQuery[]>([]);

  useEffect(() => {
    if (!step.config.itemTypeId) {
      setSavedQueries([]);
      return;
    }
    let cancelled = false;
    void listSavedQueries(spaceId, step.config.itemTypeId)
      .then((queries) => {
        if (!cancelled) setSavedQueries(queries);
      })
      .catch(() => {
        if (!cancelled) setSavedQueries([]);
      });
    return () => {
      cancelled = true;
    };
  }, [spaceId, step.config.itemTypeId]);

  const onPickSaved = (id: string) => {
    const q = savedQueries.find((x) => x.id === id);
    if (!q) return;
    onConfigChange({ jql: q.jql, mode: q.mode });
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="small">
      <Typography.Text strong>Тип записи</Typography.Text>
      <Select
        placeholder="Выберите тип записи"
        value={step.config.itemTypeId}
        options={itemTypes.map((it) => ({ value: it.id, label: it.name }))}
        onChange={(v) => onConfigChange({ itemTypeId: v })}
        style={{ width: '100%' }}
        showSearch
        optionFilterProp="label"
      />

      <Typography.Text strong>JQL</Typography.Text>
      {step.config.itemTypeId && (
        <Select
          placeholder={
            savedQueries.length > 0
              ? 'Подставить из сохранённых JQL пространства…'
              : 'Сохранённых JQL для этого типа пока нет'
          }
          value={undefined}
          allowClear
          disabled={savedQueries.length === 0}
          onChange={(v) => v && onPickSaved(v)}
          options={savedQueries.map((q) => ({
            value: q.id,
            label: q.name,
            title: q.jql,
          }))}
          style={{ width: '100%' }}
          showSearch
          optionFilterProp="label"
        />
      )}
      <Input.TextArea
        rows={3}
        value={step.config.jql ?? ''}
        onChange={(e) => onConfigChange({ jql: e.target.value })}
        placeholder='project = ABC AND status = "In Progress"'
      />

      <Typography.Text strong>Режим</Typography.Text>
      <Select
        value={step.config.mode ?? 'create_and_update'}
        options={PULL_MODE_OPTIONS}
        onChange={(v) => onConfigChange({ mode: v })}
        style={{ width: '100%' }}
      />

      <Typography.Text strong>Выполнять от имени</Typography.Text>
      <Alert
        type="info"
        showIcon={false}
        message="Пользователь должен быть участником пространства и иметь сохранённый Jira-токен в профиле."
        style={{ fontSize: 12 }}
      />
      <Select
        placeholder="Выберите пользователя"
        value={step.config.runAsUserId}
        options={members.map((m) => ({ value: m.userId, label: `${m.displayName} (${m.login})` }))}
        onChange={(v) => onConfigChange({ runAsUserId: v })}
        style={{ width: '100%' }}
        showSearch
        optionFilterProp="label"
      />
    </Space>
  );
}

// ─── jira.push ──────────────────────────────────────────────────────────

function JiraPushForm({
  step,
  views,
  members,
  onConfigChange,
}: {
  step: Extract<EditableStep, { type: 'jira.push' }>;
  views: ViewDto[];
  members: SpaceMember[];
  onConfigChange: (p: Record<string, unknown>) => void;
}): JSX.Element {
  return (
    <Space direction="vertical" style={{ width: '100%' }} size="small">
      <Typography.Text strong>Представление</Typography.Text>
      <Select
        placeholder="Выберите представление"
        value={step.config.viewId}
        options={views.map((v) => ({ value: v.id, label: v.name }))}
        onChange={(v) => onConfigChange({ viewId: v })}
        style={{ width: '100%' }}
        showSearch
        optionFilterProp="label"
      />

      <Checkbox
        checked={step.config.changedSinceLastRun !== false}
        onChange={(e) => onConfigChange({ changedSinceLastRun: e.target.checked })}
      >
        Только записи, изменённые с прошлого запуска (рекомендуется)
      </Checkbox>

      <Typography.Text strong>Выполнять от имени</Typography.Text>
      <Alert
        type="info"
        showIcon={false}
        message="Пользователь должен быть участником пространства и иметь сохранённый Jira-токен в профиле."
        style={{ fontSize: 12 }}
      />
      <Select
        placeholder="Выберите пользователя"
        value={step.config.runAsUserId}
        options={members.map((m) => ({ value: m.userId, label: `${m.displayName} (${m.login})` }))}
        onChange={(v) => onConfigChange({ runAsUserId: v })}
        style={{ width: '100%' }}
        showSearch
        optionFilterProp="label"
      />
    </Space>
  );
}
